import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { DateField } from '../../../../components/inputs/DateField';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { mySurveysApi } from '../mySurveysApi';
import type { MySurveyTraining } from '../mySurveyTypes';

const ID = 'my-survey-training';

/**
 * A section's title is BOLD — the format Learning & Development's own forms give theirs (product
 * call, 12 Oct 2026, on the development plan form; asked for here 13 Oct). Scoped to the legend:
 * the field labels inside the section keep the regular weight every form draws them at.
 */
const SECTION_TITLE = '[&>legend]:font-semibold';

/** Today as a calendar date, `YYYY-MM-DD`, from LOCAL y/m/d — never through an instant. */
function todayCalendarDate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `YYYY-MM-DD` plus whole days, on the calendar — built from local y/m/d at noon (no DST edge). */
function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(y, m - 1, d + days, 12);
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

/**
 * Start training — or correct it — on the employee's ACCEPTED development plan: a title, a start
 * date and an end date (product call, 12 Oct 2026). ONE training per plan, so a second save
 * replaces the first; Learning & Development reads it on its Development Plan register.
 *
 * **On a training recommendation the form is the TRAINING DURATION alone** (product call, 13 Oct
 * 2026): the school and its location were given at Enroll, the step before. **The start date opens
 * on TODAY** — the day Start training is clicked — and stays editable.
 *
 * The dates are CALENDAR dates and stay strings end to end — `DateField` reads and writes
 * `YYYY-MM-DD`, and the server stores a `DateOnly`. The end may not fall before the start: the end
 * field's `min` offers nothing earlier, and the server refuses it too.
 *
 * **An EDIT asks for REMARKS, and requires them** — why the training is being changed, which
 * Learning & Development reads. Starting a training asks for none. Autogenerate leaves them alone:
 * a reason is the employee's own.
 */
export function MySurveyTrainingModal({
  surveyId,
  recommendationId,
  recommendationTitle,
  enrolledEndDate,
  training,
  onClose,
  onSaved,
}: {
  surveyId: string;
  /**
   * Set when the training is taken on a SENT TRAINING RECOMMENDATION (product call, 13 Oct 2026):
   * the form then writes that employee's own track on it, not the plan's. Absent, it is the
   * development plan's own training, as before.
   */
  recommendationId?: string;
  /** That recommendation's title — the training IS what was recommended, so it is stated, never typed. */
  recommendationTitle?: string;
  /**
   * The end date given at enrollment. A NEW training opens with it filled — unless it has already
   * passed, when a start of today could not precede it.
   */
  enrolledEndDate?: string | null;
  /** The training already recorded — an edit — or null to start one. */
  training: MySurveyTraining | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const editing = training !== null;

  const [title, setTitle] = useState(training?.title ?? '');
  // On a recommendation a NEW training starts today — the day the button was clicked; still editable.
  const [startDate, setStartDate] = useState(training?.startDate ?? (recommendationId != null ? todayCalendarDate() : ''));
  const [endDate, setEndDate] = useState(
    training?.endDate ?? (enrolledEndDate != null && enrolledEndDate >= todayCalendarDate() ? enrolledEndDate : ''),
  );
  // Why the training is being corrected — asked only on an EDIT, and required there.
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const endBeforeStart = startDate !== '' && endDate !== '' && endDate < startDate;
  const remarksMissing = editing && remarks.trim() === '';
  // On a recommendation the title is the recommendation's own — nothing to type, so nothing to require.
  const onRecommendation = recommendationId != null;
  const valid =
    (onRecommendation || title.trim() !== '') && startDate !== '' && endDate !== '' && !endBeforeStart && !remarksMissing;
  const canSave = valid && confirmed && !saving;

  /** Synthetic values, for exercising the form — a training a week out, lasting five days. */
  function generate() {
    const start = addDays(todayCalendarDate(), 7);

    // Never a field the reader has no say in: on a recommendation the title is fixed.
    if (!onRecommendation) setTitle(`Sample training ${uid().slice(0, 6)}`);
    setStartDate(start);
    setEndDate(addDays(start, 4));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      if (recommendationId != null) {
        await mySurveysApi.saveRecommendationTraining(surveyId, recommendationId, {
          startDate,
          endDate,
          remarks: editing ? remarks.trim() : null,
        });
      } else {
        await mySurveysApi.saveTraining(surveyId, {
          title: title.trim(),
          startDate,
          endDate,
          remarks: editing ? remarks.trim() : null,
        });
      }
      toast.success(editing ? 'Saved your training.' : 'Started your training.');
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your training could not be saved.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit training' : 'Start training'}
      size="lg"
      id={`${ID}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {onRecommendation ? (
          // STATED, not asked: the training is the recommendation. A locked value is text, never a disabled input.
          <div className="flex flex-col gap-1">
            <p className="text-sm text-slate-700">Training</p>
            <p id={`${ID}-title`} className="text-sm text-slate-600">
              {recommendationTitle}
            </p>
          </div>
        ) : (
        <TextField
          id={`${ID}-title`}
          label="Title of the training"
          required
          maxLength={300}
          description="The training you are taking for your development plan. Learning & Development sees it on the plan."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={title.trim() ? error?.fieldError('title') : 'Name the training.'}
          disabled={saving}
        />
        )}

        {/* The two dates are ONE answer — how long the training runs — so they share a legend. */}
        <FormFieldset legend="Training duration" className={SECTION_TITLE}>
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id={`${ID}-start-date`}
              label="Start date"
              required
              value={startDate}
              onChange={setStartDate}
              error={startDate ? error?.fieldError('startDate') : 'Choose the date the training starts.'}
              disabled={saving}
            />
            <DateField
              id={`${ID}-end-date`}
              label="End date"
              required
              value={endDate}
              onChange={setEndDate}
              min={startDate || undefined}
              error={
                !endDate
                  ? 'Choose the date the training ends.'
                  : endBeforeStart
                    ? 'The training cannot end before it starts.'
                    : error?.fieldError('endDate')
              }
              disabled={saving}
            />
          </div>
        </FormFieldset>

        {/* A CORRECTION SAYS WHY — asked only when editing a training already recorded. */}
        {editing ? (
          <Textarea
            id={`${ID}-remarks`}
            label="Remarks"
            required
            maxLength={2000}
            description="Why the training is being changed. Learning & Development reads this on your development plan."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            error={remarks.trim() ? error?.fieldError('remarks') : 'Say why the training is being changed.'}
            disabled={saving}
          />
        ) : null}
      </div>
    </Modal>
  );
}
